import React, { useState } from "react";
import clsx from "clsx";
import Icon from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiProgressUpload
} from "@mdi/js";

import { BulkUploadStatusItem } from "./bulkUpload";
import styles from "./style.module.scss";

type UploadStatusTone = "uploading" | "waiting" | "success" | "error";

const INITIAL_VISIBLE_ITEMS = 10;

interface UploadStatusListProps {
  title: string;
  items: BulkUploadStatusItem[];
  caption?: string;
  emptyMessage?: string;
  tone: UploadStatusTone;
}

const TONE_META: Record<UploadStatusTone, { icon: string; emptyLabel: string }> = {
  uploading: {
    icon: mdiProgressUpload,
    emptyLabel: "Actively processing"
  },
  waiting: {
    icon: mdiClockOutline,
    emptyLabel: "Queued for upload"
  },
  success: {
    icon: mdiCheckCircleOutline,
    emptyLabel: "Completed successfully"
  },
  error: {
    icon: mdiAlertCircleOutline,
    emptyLabel: "Needs attention"
  }
};

export default function UploadStatusList({
  title,
  items,
  caption,
  emptyMessage,
  tone
}: UploadStatusListProps) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ITEMS);
  const visibleItems = items.slice(0, visibleCount);
  const hasMoreItems = items.length > visibleCount;

  if (!items.length && !emptyMessage) {
    return null;
  }

  const toneMeta = TONE_META[tone];

  return (
    <div
      className={clsx(
        styles.statusSection,
        tone === "uploading" && styles.statusSectionUploading,
        tone === "waiting" && styles.statusSectionWaiting,
        tone === "success" && styles.statusSectionSuccess,
        tone === "error" && styles.statusSectionError
      )}>
      <div className={styles.statusSectionHeader}>
        <div className={styles.statusSectionTitleWrap}>
          <span className={styles.statusSectionIcon}>
            <Icon path={toneMeta.icon} size={0.85} />
          </span>
          <div className={styles.statusSectionTitleBlock}>
            <span>{title}</span>
            <span className={styles.statusSectionCaption}>{caption ?? toneMeta.emptyLabel}</span>
          </div>
        </div>
        <span className={styles.statusSectionCount}>{items.length}</span>
      </div>

      {items.length ? (
        <>
          <ul className={styles.statusList}>
            {visibleItems.map((item) => (
              <li
                key={item.id}
                className={clsx(
                  styles.statusRow,
                  tone === "uploading" && styles.statusRowUploading,
                  tone === "waiting" && styles.statusRowWaiting,
                  tone === "success" && styles.statusRowSuccess,
                  tone === "error" && styles.statusRowError
                )}>
                <div className={styles.statusRowText}>
                  <div className={styles.statusRowMeta}>
                    <span className={styles.statusRowPill}>Row {item.rowNumber}</span>
                    <span className={styles.statusRowTitle}>
                      {item.loadIdentifier ? `Load ${item.loadIdentifier}` : "Missing load number"}
                    </span>
                  </div>
                  {item.message ? (
                    <span className={styles.statusRowMessage}>{item.message}</span>
                  ) : null}
                </div>
                <span
                  className={clsx(
                    styles.statusBadge,
                    item.status === "uploading" && styles.statusBadgeUploading,
                    item.status === "waiting" && styles.statusBadgeWaiting,
                    item.status === "success" && styles.statusBadgeSuccess,
                    item.status === "error" && styles.statusBadgeError
                  )}>
                  {item.status}
                </span>
              </li>
            ))}
          </ul>

          {items.length > INITIAL_VISIBLE_ITEMS ? (
            <div className={styles.statusListFooter}>
              <span className={styles.statusListMeta}>
                Showing {visibleItems.length} of {items.length} rows
              </span>
              <button
                type="button"
                className={styles.statusListButton}
                onClick={() =>
                  setVisibleCount((currentCount) =>
                    hasMoreItems ? currentCount + INITIAL_VISIBLE_ITEMS : INITIAL_VISIBLE_ITEMS
                  )
                }>
                {hasMoreItems ? `Load more (${items.length - visibleItems.length})` : "Show less"}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <p className={styles.statusEmpty}>{emptyMessage}</p>
      )}
    </div>
  );
}
